import { FC, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Box, Collapse, Flex, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import {
  ApprovePhysicalPaymentCardOrderRequestInput,
  TeamMembersRequestType,
} from '__generated__/GQL';
import { formatAmount } from 'common/formatAmount';
import * as Form from 'components/_core/form';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineIcon from 'components/_core/SunshineIcon';
import DetailSidebar from 'components/DetailSidebar';

import { Request } from '../../../../hooks/useRequests';
import { PhysicalPaymentCardLimits } from '../../hooks/usePhysicalPaymentCardLimits';
import PaymentCardRequestFormFooter from '../PaymentCardRequestFormFooter';
import Summary from '../Summary';
import locales from './locales';

interface PhysicalPaymentCardRequestFormProps {
  handleRejectRequest: () => void;
  handleApproveRequest: (
    formData: Omit<
      ApprovePhysicalPaymentCardOrderRequestInput,
      'bankAccountId' | 'companyProfileId' | 'teamMembersRequestId'
    >,
  ) => void;
  isApprovingRequest: boolean;
  isRejectingRequest: boolean;
  request: Request;
  limits: PhysicalPaymentCardLimits;
}

const PhysicalPaymentCardRequestForm: FC<
  PhysicalPaymentCardRequestFormProps
> = ({
  handleApproveRequest,
  handleRejectRequest,
  isApprovingRequest,
  isRejectingRequest,
  limits,
  request,
}) => {
  const {
    defaultLimitAtmMonth,
    defaultLimitPaymentWeek,
    isOptionAtmEnabled,
    isOptionNfcEnabled,
    isOptionOnlineEnabled,
    maxLimitAtmMonth,
    maxLimitPaymentWeek,
  } = limits;

  const formSchema = useMemo(() => {
    const baseFormSchema = z.object({
      isOptionAtmEnabled: z.boolean(),
      isOptionNfcEnabled: z.boolean(),
      isOptionOnlineEnabled: z.boolean(),
      limitAtmMonth: z.number(),
      limitPaymentWeek: z
        .number({ invalid_type_error: locales.required })
        .min(0, locales.required)
        .max(
          maxLimitPaymentWeek,
          locales.formatString(locales.limitPaymentWeek.error, {
            max: formatAmount({
              currency: 'EUR',
              forcePlusSign: false,
              value: maxLimitPaymentWeek,
              withNoMinorUnitDigits: true,
            }),
          }),
        ),
    });

    const withoutAtmOptionformSchema = baseFormSchema.extend({
      isOptionAtmEnabled: z.literal<boolean>(false),
      limitAtmMonth: z.number().optional().default(defaultLimitAtmMonth),
    });

    const withAtmOptionformSchema = baseFormSchema.extend({
      isOptionAtmEnabled: z.literal<boolean>(true),
      limitAtmMonth: z
        .number({ invalid_type_error: locales.required })
        .min(0, locales.required)
        .max(
          maxLimitAtmMonth,
          locales.formatString(locales.limitPaymentWeek.error, {
            max: formatAmount({
              currency: 'EUR',
              forcePlusSign: false,
              value: maxLimitAtmMonth,
              withNoMinorUnitDigits: true,
            }),
          }),
        ),
    });

    return z.union([withoutAtmOptionformSchema, withAtmOptionformSchema]);
  }, [defaultLimitAtmMonth, maxLimitAtmMonth, maxLimitPaymentWeek]);

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    register,
  } = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      isOptionAtmEnabled,
      isOptionNfcEnabled,
      isOptionOnlineEnabled,
      limitAtmMonth: defaultLimitAtmMonth,
      limitPaymentWeek: defaultLimitPaymentWeek,
    },
    mode: 'onChange',
    resolver: zodResolver(formSchema),
  });

  const onFormSubmit = handleSubmit((values) => handleApproveRequest(values));

  return (
    <Box as="form" display="contents" onSubmit={onFormSubmit}>
      <DetailSidebar.Body paddingBottom="space-16" paddingX="space-16">
        <Summary request={request} />
        <VStack alignItems="stretch" gap="space-24">
          <Form.Field label={locales.settingLimits}>
            <SunshineCard.Group>
              <SunshineCard>
                <SunshineCard.Content spacing="space-8">
                  <SunshineCard.Title>
                    <SunshineIcon marginRight="space-8" name="card-recto" />
                    {locales.limitPaymentWeek.label}
                  </SunshineCard.Title>

                  <Form.Field
                    error={errors.limitPaymentWeek?.message}
                    hint={locales.formatString(locales.limitPaymentWeek.hint, {
                      max: formatAmount({
                        currency: 'EUR',
                        forcePlusSign: false,
                        value: maxLimitPaymentWeek,
                        withNoMinorUnitDigits: false,
                      }),
                    })}
                    width="100%"
                  >
                    <Form.Input
                      type="number"
                      {...register('limitPaymentWeek', { valueAsNumber: true })}
                    />
                  </Form.Field>
                </SunshineCard.Content>
              </SunshineCard>

              <SunshineCard>
                <SunshineCard.Content spacing="space-8">
                  <Flex
                    alignItems="center"
                    gap="space-12"
                    justifyContent="space-between"
                  >
                    <SunshineCard.Title>
                      <SunshineIcon marginRight="space-8" name="card-recto" />
                      {locales.limitAtmMonth.label}
                    </SunshineCard.Title>

                    <Controller
                      control={control}
                      name="isOptionAtmEnabled"
                      render={({ field: { name, onChange, value } }) => (
                        <Form.SunshineSwitch
                          id={name}
                          isChecked={value}
                          onChange={onChange}
                        />
                      )}
                    />
                  </Flex>

                  <Controller
                    control={control}
                    name="isOptionAtmEnabled"
                    render={({ field: { value } }) => (
                      <Collapse animateOpacity in={value}>
                        <Form.Field
                          error={errors.limitAtmMonth?.message}
                          hint={locales.formatString(
                            locales.limitAtmMonth.hint,
                            {
                              max: formatAmount({
                                currency: 'EUR',
                                forcePlusSign: false,
                                value: maxLimitAtmMonth,
                                withNoMinorUnitDigits: false,
                              }),
                            },
                          )}
                        >
                          <Form.Input
                            type="number"
                            {...register('limitAtmMonth', {
                              valueAsNumber: true,
                            })}
                          />
                        </Form.Field>
                      </Collapse>
                    )}
                  />
                </SunshineCard.Content>
              </SunshineCard>
            </SunshineCard.Group>
          </Form.Field>

          <Form.Field label={locales.cardOptions}>
            <SunshineCard.Group>
              <Controller
                control={control}
                name="isOptionNfcEnabled"
                render={({ field: { name, onChange, value } }) => (
                  <SunshineCard
                    alignItems="center"
                    id={name}
                    isChecked={value}
                    onChange={onChange}
                    title={locales.isOptionNfcEnabled.label}
                    variant="switch"
                  />
                )}
              />

              <Controller
                control={control}
                name="isOptionOnlineEnabled"
                render={({ field: { name, onChange, value } }) => (
                  <SunshineCard
                    alignItems="center"
                    id={name}
                    isChecked={value}
                    onChange={onChange}
                    title={locales.isOptionOnlineEnabled.label}
                    variant="switch"
                  />
                )}
              />
            </SunshineCard.Group>
          </Form.Field>
        </VStack>
      </DetailSidebar.Body>
      <DetailSidebar.Footer gap="space-0" padding="space-16">
        <PaymentCardRequestFormFooter
          isApprovalDisabled={!isValid || isRejectingRequest}
          isApprovalLoading={isApprovingRequest}
          isRejectionDisabled={isApprovingRequest}
          isRejectionLoading={isRejectingRequest}
          onReject={handleRejectRequest}
          type={TeamMembersRequestType.PhysicalPaymentCardOrder}
        />
      </DetailSidebar.Footer>
    </Box>
  );
};

export default PhysicalPaymentCardRequestForm;
